import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface AnalysisReport {
  estimateId: string;
  lastRunTime: number;
  report: string;
}

interface AnalysisReportState {
  reports: { [key: string]: AnalysisReport };
  addReport: (estimateId: string, report: string) => void;
  getReport: (estimateId: string) => AnalysisReport | null;
  isOnCooldown: (estimateId: string) => boolean;
  getCooldownTimeRemaining: (estimateId: string) => number;
  clearReport: (estimateId: string) => void;
}

const COOLDOWN_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

export const useAnalysisReportStore = create<AnalysisReportState>()(
  persist(
    (set, get) => ({
      reports: {},
      addReport: (estimateId: string, report: string) => {
        set(state => ({
          reports: {
            ...state.reports,
            [estimateId]: {
              estimateId,
              lastRunTime: Date.now(),
              report,
            },
          },
        }));
      },
      getReport: (estimateId: string) => {
        return get().reports[estimateId] || null;
      },
      isOnCooldown: (estimateId: string) => {
        const report = get().reports[estimateId];
        if (!report) return false;
        return Date.now() - report.lastRunTime < COOLDOWN_DURATION;
      },
      getCooldownTimeRemaining: (estimateId: string) => {
        const report = get().reports[estimateId];
        if (!report) return 0;
        const timeElapsed = Date.now() - report.lastRunTime;
        return Math.max(0, COOLDOWN_DURATION - timeElapsed);
      },
      clearReport: (estimateId: string) => {
        set(state => {
          const { [estimateId]: _, ...rest } = state.reports;
          return { reports: rest };
        });
      },
    }),
    {
      name: 'analysis-report-storage',
    }
  )
);
